// The cells the leaderboard tables share: the ranked or audited entity as a
// link to its own card (EntityCell / EntityLink), and the actor behind an
// audit row (ActorCell). Port of the task's design artifact
// leaderboard-entity-links.html: a player is a round avatar linking to
// /players/:id, a guild is a 7px emblem linking to /guilds/:id.
//
// Both shapes live here rather than in each tab because the audit table is the
// one place that cannot tell them apart from the board alone: on a
// scope=guild_aggregated board a client submit is logged against the player
// who sent it while admin moderation is logged against the guild, so the kind
// travels per row (AuditEntry.entity_kind) and the link follows it.
//
// An entity that did not resolve — a deleted player, a purged guild, a page
// read without ?expand=entity — is not an error and not a broken link: the row
// falls back to the short mono id it carried before there was a name to show.

import { Link } from "react-router-dom";

import { gradientFor, initialsFor } from "../../components/Avatar/avatarLook";
import type { AuditEntry, EntityKind } from "./types";
import styles from "./EntityCell.module.css";

function shortId(id: string | undefined, n = 8): string {
  if (!id) return "—";
  return id.replace(/-/g, "").slice(0, n);
}

/** A resolved entity: who the row is about, and which card names them. */
interface EntityRef {
  id: string;
  name: string;
  kind: EntityKind;
}

interface EntityProps {
  gameId: string;
  id: string;
  /** Display name; absent or empty means the id did not resolve. */
  name?: string;
  /** Entity kind; absent means the id did not resolve. */
  kind?: EntityKind;
}

function cardPath(gameId: string, entity: EntityRef): string {
  const seg = entity.kind === "guild" ? "guilds" : "players";
  return `/projects/${encodeURIComponent(gameId)}/${seg}/${encodeURIComponent(entity.id)}`;
}

/** Narrow the loose props to a resolved entity, or null when there is none. */
function resolve({ id, name, kind }: EntityProps): EntityRef | null {
  if (!id || !name || !kind) return null;
  return { id, name, kind };
}

/**
 * The unresolved fallback: the id, shortened, exactly as it read before.
 * The tooltip says why there is no name AND carries the full id — the row it
 * stands in has nowhere else to show one.
 */
function UnresolvedId({ id }: { id: string }) {
  return (
    <span className={styles.unresolved} title={`${id} — not found (deleted or purged)`}>
      {shortId(id)}
    </span>
  );
}

/**
 * The entity's name as a link to its card, with no avatar — the shape the
 * Overview "Top score" caption uses.
 *
 * The click is stopped from bubbling: an audit row is itself clickable (it
 * expands into the event details), and following a link is not a request to
 * also open that.
 */
export function EntityLink(props: EntityProps) {
  const entity = resolve(props);
  if (!entity) return <UnresolvedId id={props.id} />;
  return (
    <Link
      to={cardPath(props.gameId, entity)}
      className={styles.link}
      onClick={(e) => e.stopPropagation()}
    >
      {entity.name}
    </Link>
  );
}

/**
 * The Actor column of an audit table: the admin's email once ?expand=entity
 * resolved it, the bare id when it could not (a deleted account, an actor
 * lookup that failed), and a dash for client/s2s — neither channel carries an
 * actor identity apart from the entity itself.
 *
 * It lives beside EntityCell because the two audit tables — the Audit tab and
 * the Overview card — render the same row and had this ternary twice.
 */
export function ActorCell({ row }: { row: AuditEntry }) {
  if (row.channel !== "admin") return <span className={styles.muted}>—</span>;
  if (row.actor_email) {
    return <span className={styles.actorEmail}>{row.actor_email}</span>;
  }
  if (row.admin_user_id) {
    return <span className={styles.unresolved}>{shortId(row.admin_user_id)}</span>;
  }
  return <span className={styles.muted}>—</span>;
}

/**
 * Avatar + linked name, the shape every leaderboard table uses for its entity
 * column. `size="sm"` is the 24px variant of the Overview Top-5 table.
 */
export function EntityCell({ size = "md", ...props }: EntityProps & { size?: "md" | "sm" }) {
  const entity = resolve(props);
  if (!entity) return <UnresolvedId id={props.id} />;
  // The gradient is seeded by the name, exactly as <Avatar> seeds it
  // everywhere else, so the same player carries the same colours here, in the
  // Players list and on their card.
  const avatarCls = [
    styles.avatar,
    size === "sm" ? styles.avatarSm : "",
    entity.kind === "guild" ? styles.emblem : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={styles.cell}>
      <span
        className={avatarCls}
        style={{ background: gradientFor(entity.name) }}
        aria-hidden="true"
      >
        {initialsFor(entity.name)}
      </span>
      <EntityLink {...props} />
    </div>
  );
}
